import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Pencil } from 'lucide-react';

const MAX_DAY_NAME_LENGTH = 100;

export interface DayNameEditPopoverProps {
  /** Controlled open state */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayNumber: number;
  /** Initial value when popover opens (e.g. current day name) */
  value: string;
  /** Called when user saves; receives the value to save (use this for API call) */
  onSave: (value: string) => Promise<void>;
  onClear: () => Promise<void>;
  saving: boolean;
  hasExistingName: boolean;
  /** Trigger element; omit to use default pencil button */
  trigger?: React.ReactNode;
  /** Optional class for the trigger button */
  triggerClassName?: string;
}

export function DayNameEditPopover({
  open,
  onOpenChange,
  dayNumber,
  value,
  onSave,
  onClear,
  saving,
  hasExistingName,
  trigger,
  triggerClassName,
}: DayNameEditPopoverProps) {
  const [draftValue, setDraftValue] = useState(value);

  useEffect(() => {
    if (open) {
      setDraftValue(value);
    }
  }, [open, value]);

  const defaultTrigger = (
    <button
      type="button"
      className={triggerClassName ?? 'ml-1 rounded p-1.5 hover:bg-muted/80 transition-colors'}
      onClick={(e) => e.stopPropagation()}
      aria-label="Edit day name"
    >
      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger ?? defaultTrigger}</DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Day {dayNumber} display name</DialogTitle>
          <DialogDescription>
            Give this day a label (e.g. &quot;Detox Day&quot;) or leave blank to show &quot;Day {dayNumber}&quot;.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor={`day-name-${dayNumber}`} className="sr-only">
              Display name
            </Label>
            <Input
              id={`day-name-${dayNumber}`}
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              placeholder="e.g. Detox Day"
              maxLength={MAX_DAY_NAME_LENGTH}
              className="h-10 text-sm"
              disabled={saving}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {draftValue.length}/{MAX_DAY_NAME_LENGTH}
            </span>
            <DialogFooter className="flex gap-2 sm:gap-0">
              {hasExistingName && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-foreground"
                  disabled={saving}
                  onClick={onClear}
                >
                  Clear
                </Button>
              )}
              <Button
                size="sm"
                disabled={saving}
                onClick={() => onSave(draftValue.trim() || '')}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
